import { isDefined, isFunction, isString, isTruthy } from '@fabric-app/utils/guards';
import { isCommentWithTextAnnotation } from '../../comments/utils/annotationGuards.js';
import { getSimilarity, mapStringRanges } from '@fabric-app/utils/text';
import { escapeAttribute, getTextNodesWithinSelection } from '@fabric-app/utils/element';
import { addFabricEventListener } from '../../hooks/useFabricEvent.js';
import { setIsCommentOrphan } from '../../comments/stores/orphanCommentsStore.js';
import { v4 } from 'uuid';
import Color from 'color';
import { colorToRGBCssString } from '@fabric-app/ui/theme/utils';
import { REALIGNMENT_SEARCH_WINDOW, SCORE_WEIGHT_TEXT, SCORE_WEIGHT_CONTEXT, REALIGNMENT_SIMILARITY_THRESHOLD } from '../config.js';

function isRotationAround90(transformValue, tolerance = 10) {
  if (!transformValue || transformValue === "none") {
    return false;
  }
  const matrixValues = transformValue.match(/matrix\((.+)\)/);
  if (!matrixValues) {
    return false;
  }
  const parts = matrixValues[1].split(",").map(parseFloat);
  const [a, b] = parts;
  const angleRad = Math.atan2(b, a);
  const angleDeg = angleRad * (180 / Math.PI);
  const normalizedAngle = (angleDeg % 360 + 360) % 360;
  const isAround90 = Math.abs(normalizedAngle - 90) <= tolerance;
  const isAround270 = Math.abs(normalizedAngle - 270) <= tolerance;
  return isAround90 || isAround270;
}
const colorToRGBStyle = (color) => {
  return {
    "--annotation-color": colorToRGBCssString(new Color(color))
  };
};
const CHUNK_SIZE = 5e3;
const BLOCK_ELEMENT_SELECTOR = "article, aside, blockquote, dd, div, dl, dt, fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, header, hr, li, main, nav, ol, p, pre, section, table, tfoot, ul";
const findClosestBlockAncestor = (node, rootElement) => {
  if (!node) return null;
  const parent = node.parentElement;
  if (!parent) return null;
  const ancestor = parent.closest(BLOCK_ELEMENT_SELECTOR);
  if (ancestor && rootElement.contains(ancestor)) return ancestor;
  return null;
};
class DOMModel {
  // ------------------------------
  // ----- constructor / init -----
  // ------------------------------
  constructor(doc, options = {
    mode: "static",
    chunkSize: CHUNK_SIZE
  }) {
    this.widgets = [];
    this.newWidgets = [];
    this.isProcessingQueue = false;
    this.updateQueue = [];
    this.replacementChanges = [];
    this.cleanup = null;
    this.domMap = /* @__PURE__ */ new Map();
    this.chunks = [];
    this.doc = doc;
    this.options = options;
    if (this.options.mode === "dynamic") {
      const { totalChunks } = this.options;
      this.chunks = new Array(totalChunks).fill(null);
      this._parseDynamicDocument();
    } else {
      this._parseStaticDocument();
    }
    const unsubDelete = addFabricEventListener("fabric-annotation-action-delete", (event) => {
      const { resourceId, annotationId } = event.detail;
      if (!resourceId || !annotationId) return;
      this.newWidgets = this.widgets.filter((w) => !w.meta?.isPlaceholder || w.meta?.resourceId !== resourceId || w.id !== annotationId);
      this.update();
    });
    const unsubLink = addFabricEventListener("fabric-annotation-action-link", (event) => {
      const { resourceId, annotation } = event.detail;
      if (!resourceId || !annotation) return;
      this.newWidgets = this.widgets.filter((w) => !w.meta?.isPlaceholder && w.meta?.resourceId !== resourceId && w.id !== annotation.id);
      this.update();
    });
    this.cleanup = () => {
      unsubDelete();
      unsubLink();
    };
  }
  // ---------------------------
  // ----- utils / helpers -----
  // ---------------------------
  normalizeText(text) {
    return text.replace(/\s+/g, " ").replace(/\n/g, " ");
  }
  binarySearchSegmentIdx(chunkIndex, location) {
    const segments = this.chunks[chunkIndex]?.segments ?? [];
    let low = 0;
    let high = segments.length - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const segment = segments[mid];
      if (location >= segment.start && location < segment.end) {
        return mid;
      }
      if (location < segment.start) {
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }
    return -1;
  }
  findSegmentInChunk(chunkIndex, location) {
    const chunk = this.chunks[chunkIndex];
    if (!chunk) return null;
    const segmentIdx = this.binarySearchSegmentIdx(chunkIndex, location);
    if (segmentIdx === -1) return null;
    return chunk.segments[segmentIdx];
  }
  mapRawToNormalizedOffset(rawText, rawOffset, addedWhitespace = false) {
    const rawSubstring = rawText.substring(0, rawOffset);
    const normalizedOffset = this.normalizeText(rawSubstring).length;
    return normalizedOffset + (addedWhitespace ? 1 : 0);
  }
  mapNormalizedToRawOffset(rawText, normalizedOffset, addedWhitespace = false) {
    if (normalizedOffset - (addedWhitespace ? 1 : 0) <= 0) {
      return 0;
    }
    const fullNormalizedText = (addedWhitespace ? " " : "") + this.normalizeText(rawText);
    if (normalizedOffset >= fullNormalizedText.length) {
      return rawText.length;
    }
    let rawIndex = 0;
    let normalizedIndex = addedWhitespace ? 1 : 0;
    let wasLastCharWhitespace = true;
    while (rawIndex < rawText.length && normalizedIndex < normalizedOffset) {
      const rawChar = rawText[rawIndex];
      const isCurrentCharWhitespace = /\s/.test(rawChar);
      if (isCurrentCharWhitespace) {
        if (!wasLastCharWhitespace) {
          normalizedIndex++;
        }
        wasLastCharWhitespace = true;
      } else {
        normalizedIndex++;
        wasLastCharWhitespace = false;
      }
      if (normalizedIndex === normalizedOffset) {
        return rawIndex + 1;
      }
      rawIndex++;
    }
    return rawText.length;
  }
  addWidgetChunksToSet(widget, set) {
    const { startChunk, endChunk } = widget;
    if (isDefined(startChunk) && isDefined(endChunk)) {
      for (let i = startChunk; i <= endChunk; i++) {
        set.add(i);
      }
    }
  }
  findDirtyChunks(nextWidgets) {
    const dirtyIndexes = /* @__PURE__ */ new Set();
    const oldWidgetIds = new Set(this.widgets.map((w) => w.id));
    const newWidgetIds = new Set(nextWidgets.map((w) => w.id));
    for (const oldWidget of this.widgets) {
      if (!newWidgetIds.has(oldWidget.id)) {
        this.addWidgetChunksToSet(oldWidget, dirtyIndexes);
      }
    }
    for (const newWidget of nextWidgets) {
      if (!oldWidgetIds.has(newWidget.id)) {
        this.addWidgetChunksToSet(newWidget, dirtyIndexes);
      }
    }
    return dirtyIndexes;
  }
  isWidgetInChunks(widget, chunkIndexes) {
    const { startChunk, endChunk } = widget;
    if (isDefined(startChunk) && isDefined(endChunk)) {
      for (let i = startChunk; i <= endChunk; i++) {
        if (chunkIndexes.has(i)) return true;
      }
    }
    return false;
  }
  // ------------------------------
  // ------- parse / update -------
  // ------------------------------
  _processQueue() {
    if (this.isProcessingQueue) return;
    const task = this.updateQueue.shift();
    if (!task) return;
    this.isProcessingQueue = true;
    task();
    this.isProcessingQueue = false;
    Promise.resolve().then(() => this._processQueue());
  }
  _parseStaticDocument() {
    if (this.options.mode !== "static") {
      throw new Error("DOMModel is not in static mode");
    }
    const walker = document.createTreeWalker(this.doc, NodeFilter.SHOW_TEXT);
    let estimatedSize = 0;
    let chunkIndex = 0;
    let nodesForCurrentChunk = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeLength = node.textContent?.trim().length ?? 0;
      nodesForCurrentChunk.push(node);
      estimatedSize += nodeLength;
      if (estimatedSize >= this.options.chunkSize) {
        this.chunks[chunkIndex] = null;
        this._parseNodesIntoChunk(nodesForCurrentChunk, chunkIndex);
        chunkIndex++;
        nodesForCurrentChunk = [];
        estimatedSize = 0;
      }
    }
    if (nodesForCurrentChunk.length > 0) {
      this._parseNodesIntoChunk(nodesForCurrentChunk, chunkIndex);
    }
  }
  _parseNodesIntoChunk(textNodes, chunkIndex) {
    let location = 0;
    let canonicalText = "";
    let text = "";
    const segments = [];
    const nodes = [];
    let segmentIndex = 0;
    let previousNode = null;
    for (let i = 0; i < textNodes.length; i++) {
      const node = textNodes[i];
      const parent = node.parentElement;
      if (parent?.closest("style, script, noscript") || !parent) continue;
      const segmentCanonicalText = node.textContent ?? "";
      let segmentText = this.normalizeText(node.textContent ?? "");
      let block = false;
      let addedWhitespace = false;
      if (!segmentText.trim()) continue;
      if (previousNode) {
        const blockAncestor = findClosestBlockAncestor(node, this.doc);
        const prevBlockAncestor = findClosestBlockAncestor(previousNode, this.doc);
        if (blockAncestor !== prevBlockAncestor) {
          block = true;
        }
        const previousParent = previousNode.parentElement;
        if (this.options.checkItemPositionsForSegmentSpacing && previousParent) {
          const previousVertical = isRotationAround90(getComputedStyle(previousParent).transform);
          const currentVertical = isRotationAround90(getComputedStyle(parent).transform);
          const offset = previousVertical && currentVertical ? "offsetLeft" : "offsetTop";
          const size = previousVertical && currentVertical ? "offsetWidth" : "offsetHeight";
          const previousOffset = previousParent[offset];
          const currentOffset = parent[offset];
          const diff = Math.abs(previousOffset - currentOffset);
          if (diff > parent[size] / 2) {
            block = true;
          }
        }
      }
      if (block && !segmentText.startsWith(" ")) {
        segmentText = " " + segmentText;
        addedWhitespace = true;
      }
      const segment = {
        canonicalText: segmentCanonicalText,
        text: segmentText,
        start: location,
        end: location + segmentText.length,
        node,
        block,
        addedWhitespace
      };
      location += segmentText.length;
      canonicalText += segmentCanonicalText;
      text += segmentText;
      segments.push(segment);
      nodes.push(node);
      this.domMap.set(node, {
        chunkIndex,
        segmentIndex
      });
      segmentIndex++;
      previousNode = node;
    }
    const chunk = {
      canonicalText,
      text,
      segments,
      nodes,
      length: location
    };
    this.chunks[chunkIndex] = chunk;
  }
  _parseDynamicDocument() {
    if (this.options.mode !== "dynamic") throw new Error("DOMModel is not in dynamic mode");
    const chunkElements = this.doc.querySelectorAll(this.options.chunkFindSelector);
    for (const chunkElement of chunkElements) {
      if (!(chunkElement instanceof HTMLElement) || chunkElement.textContent?.trim() === "") continue;
      const chunkIndexAttribute = chunkElement.getAttribute(this.options.chunkAttribute);
      if (!chunkIndexAttribute) continue;
      const chunkIndex = parseInt(chunkIndexAttribute);
      if (isNaN(chunkIndex)) continue;
      this.parseDynamicChunk(chunkElement, chunkIndex);
    }
  }
  parseDynamicChunk(element, chunkIndex) {
    if (this.options.mode !== "dynamic") return;
    const chunkSelector = this.options.chunkSelectorForTextNode;
    const applyTask = () => {
      this._restoreChunk(chunkIndex);
      const target = chunkSelector ? element.querySelector(chunkSelector) : element;
      if (!target) return;
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        nodes.push(walker.currentNode);
      }
      this._parseNodesIntoChunk(nodes, chunkIndex);
      this._restoreChunk(chunkIndex);
      const set = /* @__PURE__ */ new Set([
        chunkIndex
      ]);
      const appliableWidgets = this.widgets.filter((widget) => this.isWidgetInChunks(widget, set));
      this._applyWidgets(appliableWidgets);
    };
    this.updateQueue.push(applyTask);
    this._processQueue();
  }
  unmountChunk(chunkIndex) {
    if (this.options.mode !== "dynamic") return;
    const applyTask = () => {
      this._restoreChunk(chunkIndex);
      if (this.chunks[chunkIndex]) for (const segment of this.chunks[chunkIndex].segments) {
        this.domMap.delete(segment.node);
      }
      this.replacementChanges = this.replacementChanges.filter((c) => c.chunkIndex !== chunkIndex);
      this.chunks[chunkIndex] = null;
    };
    this.updateQueue.push(applyTask);
    this._processQueue();
  }
  // ----------------------------------
  // ----- annotation / selection -----
  // ----------------------------------
  _restoreChunk(chunkIndex) {
    const chunk = this.chunks[chunkIndex];
    if (!chunk) return;
    const relevantChanges = this.replacementChanges.filter((c) => c.chunkIndex === chunkIndex);
    for (let i = relevantChanges.length - 1; i >= 0; i--) {
      const change = relevantChanges[i];
      const { oldNode, oldSegment, newNodes, spanNodes, segmentIndex } = change;
      const firstSpan = spanNodes[0];
      const parent = firstSpan.parentElement;
      if (!parent) continue;
      parent.insertBefore(oldNode, firstSpan);
      chunk.segments.splice(segmentIndex, newNodes.length, oldSegment);
      chunk.nodes.splice(segmentIndex, newNodes.length, oldNode);
      for (let i2 = 0; i2 < newNodes.length; i2++) {
        const node = newNodes[i2];
        this.domMap.delete(node);
        node.parentElement?.removeChild(node);
      }
      for (let i2 = 0; i2 < spanNodes.length; i2++) {
        parent.removeChild(spanNodes[i2]);
      }
      this.domMap.set(oldNode, {
        chunkIndex,
        segmentIndex
      });
    }
    this.replacementChanges = this.replacementChanges.filter((c) => c.chunkIndex !== chunkIndex);
  }
  _updateModelForNodeReplacement(originalNode, newNodes, spanNodes) {
    const oldNodeInfo = this.domMap.get(originalNode);
    if (!oldNodeInfo) return;
    const { chunkIndex, segmentIndex } = oldNodeInfo;
    const chunk = this.chunks[chunkIndex];
    const oldSegment = chunk?.segments[segmentIndex];
    if (!oldSegment || !chunk) return;
    const newSegments = [];
    let currentLocation = oldSegment.start;
    let newSegmentIndex = segmentIndex;
    for (const [i, newNode] of newNodes.entries()) {
      const canonicalText = newNode.textContent ?? "";
      let text = this.normalizeText(canonicalText);
      const block = oldSegment.block && i === 0;
      const addedWhitespace = oldSegment.addedWhitespace && i === 0;
      if (addedWhitespace) {
        text = " " + text;
      }
      const segment = {
        canonicalText,
        text,
        start: currentLocation,
        end: currentLocation + text.length,
        node: newNode,
        block,
        addedWhitespace
      };
      newSegments.push(segment);
      currentLocation += text.length;
      this.domMap.set(newNode, {
        chunkIndex,
        segmentIndex: newSegmentIndex
      });
      newSegmentIndex++;
    }
    chunk.segments.splice(segmentIndex, 1, ...newSegments);
    chunk.nodes.splice(segmentIndex, 1, ...newNodes);
    this.replacementChanges.push({
      newNodes,
      spanNodes,
      oldNode: originalNode,
      oldSegment,
      chunkIndex,
      segmentIndex
    });
    this.domMap.delete(originalNode);
    for (let i = newSegmentIndex; i < chunk.nodes.length; i++) {
      this.domMap.set(chunk.nodes[i], {
        chunkIndex,
        segmentIndex: i
      });
    }
  }
  _realignWidgetPosition(widget) {
    const needsRealign = isDefined(widget.textBefore) || isDefined(widget.textAfter);
    const isSingleChunk = widget.startChunk === widget.endChunk;
    if (!needsRealign || !isSingleChunk || !widget.text) return widget;
    const chunk = this.chunks[widget.startChunk];
    if (!chunk) return widget;
    const searchQuery = (widget.textBefore ?? "") + widget.text + (widget.textAfter ?? "");
    if (searchQuery.length === 0) return widget;
    const expectedSearchStart = widget.locationStart - (widget.textBefore?.length ?? 0);
    const expectedSearchEnd = widget.locationEnd + (widget.textAfter?.length ?? 0);
    const searchRangeStart = Math.max(0, expectedSearchStart - REALIGNMENT_SEARCH_WINDOW);
    const searchRangeEnd = Math.min(chunk.text.length - searchQuery.length, expectedSearchEnd + REALIGNMENT_SEARCH_WINDOW);
    const candidates = [];
    if (searchRangeStart >= searchRangeEnd) return widget;
    for (let i = searchRangeStart; i <= searchRangeEnd; i++) {
      const docSlice = chunk.text.substring(i, i + widget.text.length);
      const textSimilarity = getSimilarity(widget.text, docSlice);
      if (textSimilarity >= REALIGNMENT_SIMILARITY_THRESHOLD) {
        candidates.push({
          index: i,
          textSimilarity
        });
      }
    }
    if (candidates.length === 0) {
      return widget;
    }
    const scoredCandidates = candidates.map((candidate) => {
      const { textBefore = "", textAfter = "" } = widget;
      const docBefore = chunk.text.substring(Math.max(0, candidate.index - textBefore.length), candidate.index);
      const beforeSimilarity = getSimilarity(textBefore, docBefore);
      const docAfter = chunk.text.substring(candidate.index + widget.text.length, candidate.index + widget.text.length + textAfter.length);
      const afterSimilarity = getSimilarity(textAfter, docAfter);
      const totalScore = candidate.textSimilarity * SCORE_WEIGHT_TEXT + beforeSimilarity * SCORE_WEIGHT_CONTEXT + afterSimilarity * SCORE_WEIGHT_CONTEXT;
      return {
        index: candidate.index,
        score: totalScore
      };
    });
    scoredCandidates.sort((a, b) => b.score - a.score);
    const bestMatch = scoredCandidates[0];
    if (bestMatch) {
      const newLocationStart = bestMatch.index;
      const newLocationEnd = newLocationStart + widget.text.length;
      return {
        ...widget,
        locationStart: newLocationStart,
        locationEnd: newLocationEnd
      };
    }
    return widget;
  }
  _applyWidgets(widgets) {
    const nodeToAnnotations = /* @__PURE__ */ new Map();
    const foundWidgetIds = /* @__PURE__ */ new Set();
    const realignedWidgets = widgets.map((w) => this._realignWidgetPosition(w));
    for (const widget of realignedWidgets) {
      const { startChunk, endChunk, locationStart, locationEnd } = widget;
      for (let cIdx = startChunk; cIdx <= endChunk; cIdx++) {
        const chunk = this.chunks[cIdx];
        if (!chunk) {
          foundWidgetIds.add(widget.id);
          continue;
        }
        const firstSegmentIdx = cIdx === startChunk ? this.binarySearchSegmentIdx(cIdx, locationStart) : 0;
        const lastSegmentIdx = cIdx === endChunk ? this.binarySearchSegmentIdx(cIdx, locationEnd) : chunk.segments.length - 1;
        if (firstSegmentIdx === -1 || lastSegmentIdx === -1) continue;
        for (let sIdx = firstSegmentIdx; sIdx <= lastSegmentIdx; sIdx++) {
          const segment = chunk.segments[sIdx];
          if (!segment) continue;
          const normalizedStart = Math.max(locationStart, segment.start) - segment.start;
          const normalizedEnd = Math.min(locationEnd, segment.end) - segment.start;
          const rawStart = this.mapNormalizedToRawOffset(segment.canonicalText, normalizedStart, segment.addedWhitespace);
          const rawEnd = this.mapNormalizedToRawOffset(segment.canonicalText, normalizedEnd, segment.addedWhitespace);
          if (rawStart >= rawEnd) continue;
          if (!nodeToAnnotations.has(segment.node)) {
            nodeToAnnotations.set(segment.node, []);
          }
          nodeToAnnotations.get(segment.node).push({
            range: {
              start: rawStart,
              end: rawEnd,
              owner: widget
            }
          });
          foundWidgetIds.add(widget.id);
        }
      }
    }
    for (const [originalNode, annotations] of nodeToAnnotations.entries()) {
      const parent = originalNode.parentElement;
      if (!parent || !originalNode.textContent) continue;
      const ranges = annotations.map((a) => a.range);
      const [slices, owners] = mapStringRanges(originalNode.textContent, ranges);
      const fragment = document.createDocumentFragment();
      const newTextNodes = [];
      const spanNodes = [];
      for (let i = 0; i < slices.length; i++) {
        const sliceText = slices[i];
        const sliceOwners = owners[i];
        if (sliceOwners.length > 0) {
          const span = document.createElement("span");
          span.dataset.widget = "true";
          for (const owner of sliceOwners) {
            if (!owner.attributes) continue;
            for (const [key, value] of Object.entries(owner.attributes)) {
              span.setAttribute(key, escapeAttribute(isFunction(value) ? value(span.getAttribute(key) ?? "") : value ?? ""));
            }
            if (!owner.styles) continue;
            for (const [key, value] of Object.entries(owner.styles)) {
              span.style.setProperty(key, value);
            }
          }
          const textNode = document.createTextNode(sliceText);
          span.appendChild(textNode);
          fragment.appendChild(span);
          newTextNodes.push(textNode);
          spanNodes.push(span);
        } else {
          const textNode = document.createTextNode(sliceText);
          fragment.appendChild(textNode);
          newTextNodes.push(textNode);
        }
      }
      this._updateModelForNodeReplacement(originalNode, newTextNodes, spanNodes);
      parent.replaceChild(fragment, originalNode);
    }
    for (const widget of widgets) {
      if (!widget.meta || !widget.meta.isComment || !widget.meta.resourceId || !isString(widget.meta.resourceId)) continue;
      setIsCommentOrphan(widget.id, widget.meta.resourceId, !foundWidgetIds.has(widget.id));
    }
  }
  restore() {
    for (let i = 0; i < this.chunks.length; i++) {
      this._restoreChunk(i);
    }
    this.widgets = [];
    this.cleanup?.();
  }
  setComments(newComments) {
    const nextComments = newComments.filter(isCommentWithTextAnnotation);
    const widgets = nextComments.map((c) => {
      if (!isDefined(c.annotation.text.startChunk) || !isDefined(c.annotation.text.endChunk)) return null;
      return {
        id: c.id,
        startChunk: c.annotation.text.startChunk,
        endChunk: c.annotation.text.endChunk,
        locationStart: c.annotation.text.locationStart,
        locationEnd: c.annotation.text.locationEnd,
        text: c.annotation.text.text,
        attributes: {
          "data-annotation": "true",
          "data-comment-ids": (value) => value.trim() === "" ? c.id : value + `,${c.id}`
        },
        styles: {
          ...colorToRGBStyle(c.annotation.text.color)
        },
        meta: {
          isComment: true,
          resourceId: c.resourceId
        }
      };
    }).filter(isTruthy);
    this.newWidgets = [
      ...this.widgets.filter((w) => !w.meta?.isComment),
      ...widgets
    ];
    this.update();
  }
  update() {
    const updateTask = () => {
      const dirtyChunkIndices = this.findDirtyChunks(this.newWidgets);
      if (dirtyChunkIndices.size === 0) return;
      for (const chunkIndex of dirtyChunkIndices) {
        this._restoreChunk(chunkIndex);
      }
      const widgetsToApply = this.newWidgets.filter((widget) => this.isWidgetInChunks(widget, dirtyChunkIndices));
      this._applyWidgets(widgetsToApply);
      this.widgets = this.newWidgets;
      this.newWidgets = [];
    };
    this.updateQueue.push(updateTask);
    this._processQueue();
  }
  addWidget(widget) {
    this.newWidgets = [
      ...this.widgets,
      widget
    ];
    this.update();
  }
  getBaseWidgetFromSelection() {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !selection) return null;
    const textNodes = getTextNodesWithinSelection(selection);
    if (textNodes.length === 0) return null;
    const startNodeMap = this.domMap.get(textNodes[0]);
    const endNodeMap = this.domMap.get(textNodes[textNodes.length - 1]);
    if (!startNodeMap || !endNodeMap) return null;
    const startChunk = this.chunks[startNodeMap.chunkIndex];
    const endChunk = this.chunks[endNodeMap.chunkIndex];
    if (!startChunk || !endChunk) return null;
    const startSegment = startChunk.segments[startNodeMap.segmentIndex];
    const endSegment = endChunk.segments[endNodeMap.segmentIndex];
    const normalizedStartOffset = this.mapRawToNormalizedOffset(startSegment.canonicalText, range.startOffset, startSegment.addedWhitespace);
    const normalizedEndOffset = range.endOffset === 0 ? endSegment.end - endSegment.start : this.mapRawToNormalizedOffset(endSegment.canonicalText, range.endOffset, endSegment.addedWhitespace);
    const locationStart = startSegment.start + normalizedStartOffset;
    const locationEnd = endSegment.start + normalizedEndOffset;
    const text = textNodes.map((n) => {
      const mapped = this.domMap.get(n);
      if (!mapped) return "";
      const segment = this.chunks[mapped?.chunkIndex]?.segments[mapped.segmentIndex];
      if (!segment) return "";
      if (segment === startSegment && segment === endSegment) return segment.text.substring(normalizedStartOffset, normalizedEndOffset);
      else if (segment === startSegment) return segment.text.substring(normalizedStartOffset);
      else if (segment === endSegment && range.endOffset !== 0) return segment.text.substring(0, normalizedEndOffset);
      else {
        return segment.text;
      }
    }).join("");
    return {
      id: v4(),
      text,
      locationStart,
      locationEnd,
      startChunk: startNodeMap.chunkIndex,
      endChunk: endNodeMap.chunkIndex,
      attributes: {}
    };
  }
  getChunkSize() {
    return this.options.mode === "static" ? this.options.chunkSize : "page";
  }
  setWidgets(setter) {
    this.newWidgets = setter(this.widgets);
    this.update();
  }
}

export { DOMModel, colorToRGBStyle };
//# sourceMappingURL=DOMModel.js.map
